import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ProductCard from '../../components/ProductCard/ProductCard';
import clsx from 'clsx';
import useStyles from './ProductGrid.styles';
import { calculatePLPCardSecondaryBadgeHeight, calculateSearchCardSecondaryBadge, handleSecondaryBadgeHeight, convertBooleanToGridColumn, SiteContext, } from '../../utils/common';
import { useMediaQuery } from 'react-responsive';
import SpotlightBanner from '../SpotlightBanner/SpotlightBanner';
import { useFavorites } from '../../utils/FavoritesContext';
import { CustomPaginationContext } from '../../utils/CustomPaginationContext';
import SkeletonProductCard from '../SkeletonProductCard/SkeletonProductCard';
import { Grid2, useTheme } from '@mui/material';
import { useRouter } from 'next/router';
import { product_card_redesign } from '../../utils/featureFlags';
import { useFeatureFlag } from '../../utils/useExperiment';
const ProductGrid = ({ breakpoints, products, className, cardDisplayType, noCardBorder, highlightTextMatch, addToBasket, isFromSearchCard, spotlightBanner, sendSpotlightBannerAnalytics, totalResultsCount, onProductClick, showSkeleton, setShowSkeleton, pdpRecommendationAnalytics, isFromFavorites, isFromProductCarousel, sendFavouritingProductAnalytics, activeSearchTab, isEnableTrustpilotReviews, nameTag, imageShape, }) => {
    var _a, _b, _c;
    const { classes } = useStyles();
    const { extraSmall = 12, small = 6, medium = 4, large = 3 } = breakpoints || {};
    const [lengthToSplit, setLengthToSplit] = useState();
    const [device, setDevice] = useState();
    const theme = useTheme();
    const [screenWidth, setScreenWidth] = useState();
    const { isFlyingFlowers } = useContext(SiteContext);
    const router = useRouter();
    const isProductCardRedesignEnabled = useFeatureFlag(product_card_redesign === null || product_card_redesign === void 0 ? void 0 : product_card_redesign.key);
    // CLS Fix: Get loading state from pagination context to show skeleton placeholders
    const { isLoadingMore, pendingProductCount, clearLoadingState } = useContext(CustomPaginationContext);
    // Track the height of product cards to use for skeleton cards
    const productCardRef = React.useRef(null);
    const [productCardHeight, setProductCardHeight] = React.useState(undefined);
    const isLargerViewport = useMediaQuery({ query: `(min-width: ${theme.breakpoints.values.lg}px)` });
    const isLargeTablet = useMediaQuery({ query: `(min-width: ${theme.breakpoints.values.md}px)` });
    const isTablet = useMediaQuery({ query: `(min-width: ${theme.breakpoints.values.sm}px)` });
    const isMacTablet = useMemo(() => {
        if (typeof navigator === 'undefined' || globalThis.window === undefined || typeof document === 'undefined') {
            return false;
        }
        return (navigator.userAgent.includes('Mac') &&
            'ontouchend' in document &&
            [810, 1024].includes(globalThis.window.screen.width));
    }, []);
    useEffect(() => {
        if (isFromFavorites && products.length > 0) {
            localStorage.setItem('isFavoriteToggle', 'false');
        }
    }, [products, isFromFavorites]);
    const handleBadgeHeight = useCallback(() => {
        const { split, device, screenWidthVal } = handleSecondaryBadgeHeight(isLargeTablet, isTablet, isFromSearchCard);
        setLengthToSplit(split);
        setDevice(device);
        setScreenWidth(screenWidthVal);
    }, [isLargeTablet, isTablet, isFromSearchCard]);
    // Trigger badge height calculation on mount or screen resize
    useEffect(() => {
        if (!lengthToSplit || isMacTablet)
            handleBadgeHeight();
        globalThis.window.addEventListener('resize', handleBadgeHeight);
        return () => globalThis.window.removeEventListener('resize', handleBadgeHeight);
    }, [handleBadgeHeight, lengthToSplit, isMacTablet]);
    // Measure product card height for skeleton cards
    useEffect(() => {
        const measureCardHeight = () => {
            if (productCardRef.current) {
                const height = productCardRef.current.offsetHeight;
                if (height > 0) {
                    setProductCardHeight(height);
                }
            }
        };
        // Measure on mount and when products change
        measureCardHeight();
        // Re-measure on window resize
        globalThis.window.addEventListener('resize', measureCardHeight);
        return () => globalThis.window.removeEventListener('resize', measureCardHeight);
    }, [products.length]);
    useEffect(() => {
        if (showSkeleton && setShowSkeleton)
            setShowSkeleton(false);
    }, [showSkeleton, setShowSkeleton]);
    // CLS Fix: Track previous product count to detect when new products have loaded
    const prevProductCountRef = React.useRef(products.length);
    // CLS Fix: Determine if we should show skeletons
    // Only show skeletons if loading AND products haven't increased yet
    const productsHaveLoaded = products.length > prevProductCountRef.current;
    const shouldShowSkeletons = isLoadingMore && !productsHaveLoaded && (pendingProductCount !== null && pendingProductCount !== void 0 ? pendingProductCount : 0) > 0;
    useEffect(() => {
        // When products length increases, update ref and clear loading state for next load
        if (products.length > prevProductCountRef.current) {
            if (clearLoadingState)
                clearLoadingState();
        }
        prevProductCountRef.current = products.length;
    }, [products.length, clearLoadingState]);
    const productsList = useMemo(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        let productsData = [];
        if (lengthToSplit) {
            for (let i = 0; i < products.length; i = i + lengthToSplit) {
                let sortedProducts;
                let lengthOfTtile;
                const hasBadge = (_a = products.slice(i, i + lengthToSplit)) === null || _a === void 0 ? void 0 : _a.some((product) => { var _a; return !!((_a = product.images[0]) === null || _a === void 0 ? void 0 : _a.secondaryBadge); }); // check is secondary badge is in the cards row
                const secondaryBadgeProducts = (_b = products
                    .slice(i, i + lengthToSplit)) === null || _b === void 0 ? void 0 : _b.filter((products) => { var _a; return !!((_a = products === null || products === void 0 ? void 0 : products.images[0]) === null || _a === void 0 ? void 0 : _a.secondaryBadge); });
                if (!!secondaryBadgeProducts && (secondaryBadgeProducts === null || secondaryBadgeProducts === void 0 ? void 0 : secondaryBadgeProducts.length) > 1) {
                    /** identify max lenght of a secondary badge */
                    sortedProducts = secondaryBadgeProducts.sort((a, b) => {
                        var _a, _b, _c, _d;
                        if (!!((_b = (_a = a === null || a === void 0 ? void 0 : a.images[0]) === null || _a === void 0 ? void 0 : _a.secondaryBadge) === null || _b === void 0 ? void 0 : _b.title) && !!((_d = (_c = b === null || b === void 0 ? void 0 : b.images[0]) === null || _c === void 0 ? void 0 : _c.secondaryBadge) === null || _d === void 0 ? void 0 : _d.title)) {
                            return a.images[0].secondaryBadge.title.length < b.images[0].secondaryBadge.title.length ? 1 : -1;
                        }
                        else
                            return 1;
                    });
                }
                else
                    sortedProducts = [...secondaryBadgeProducts];
                if (!!(sortedProducts === null || sortedProducts === void 0 ? void 0 : sortedProducts.length)) {
                    lengthOfTtile = isFromSearchCard
                        ? (_f = (_e = (_d = (_c = sortedProducts[0]) === null || _c === void 0 ? void 0 : _c.images[0]) === null || _d === void 0 ? void 0 : _d.secondaryBadge) === null || _e === void 0 ? void 0 : _e.title) === null || _f === void 0 ? void 0 : _f.split(' ').join('').length
                        : (_k = (_j = (_h = (_g = sortedProducts[0]) === null || _g === void 0 ? void 0 : _g.images[0]) === null || _h === void 0 ? void 0 : _h.secondaryBadge) === null || _j === void 0 ? void 0 : _j.title) === null || _k === void 0 ? void 0 : _k.length;
                    if (isFromSearchCard && device && lengthOfTtile) {
                        lengthOfTtile = calculateSearchCardSecondaryBadge(device, lengthOfTtile);
                    }
                    else if (device && lengthOfTtile) {
                        lengthOfTtile = calculatePLPCardSecondaryBadgeHeight(device, lengthOfTtile);
                    }
                }
                for (let j = i; j < i + lengthToSplit; j++) {
                    if (j < products.length) {
                        productsData.push(Object.assign(Object.assign({}, products[j]), { hasProductBadge: hasBadge, heightOfBadge: lengthOfTtile }));
                    }
                }
            }
        }
        return productsData;
    }, [device, lengthToSplit, products, isFromSearchCard]);
    const shouldShowFavorites = useMemo(() => {
        var _a;
        if (isFlyingFlowers) {
            return false;
        }
        // Don't show favorites on search cards or when rendered inside a product carousel
        const isFromProductListing = !isFromSearchCard && !isFromProductCarousel;
        const isInternationalPage = (_a = router.asPath) === null || _a === void 0 ? void 0 : _a.includes('/international');
        const isOnInspirationTab = activeSearchTab === 'Inspiration';
        return isFromProductListing && !isInternationalPage && !isOnInspirationTab;
    }, [isFlyingFlowers, isFromSearchCard, isFromProductCarousel, router === null || router === void 0 ? void 0 : router.asPath, activeSearchTab]);
    const productsLength = (_a = productsList === null || productsList === void 0 ? void 0 : productsList.length) !== null && _a !== void 0 ? _a : 0;
    const totalResultsLength = totalResultsCount !== null && totalResultsCount !== void 0 ? totalResultsCount : 0;
    const baseDesktopPos = (_b = spotlightBanner === null || spotlightBanner === void 0 ? void 0 : spotlightBanner.desktopPosition) !== null && _b !== void 0 ? _b : -1;
    const mobilePos = (_c = spotlightBanner === null || spotlightBanner === void 0 ? void 0 : spotlightBanner.mobilePosition) !== null && _c !== void 0 ? _c : -1;
    const tabletPosition = baseDesktopPos * 2;
    const largeTabletPosition = baseDesktopPos * 3;
    const largeViewportPosition = baseDesktopPos * 4;
    const spotlightBannerPosition = React.useMemo(() => {
        const resolvePosition = (configuredPos, minProducts) => {
            if (configuredPos > 0) {
                return Math.min(totalResultsLength, configuredPos);
            }
            return productsLength >= minProducts ? minProducts : -1;
        };
        if (isLargerViewport) {
            return resolvePosition(largeViewportPosition, 8);
        }
        if (isLargeTablet) {
            return resolvePosition(largeTabletPosition, 6);
        }
        if (isTablet) {
            return resolvePosition(tabletPosition, 4);
        }
        // Mobile fallback
        return resolvePosition(mobilePos, 5);
    }, [
        isLargerViewport,
        isLargeTablet,
        isTablet,
        mobilePos,
        totalResultsLength,
        productsLength,
        tabletPosition,
        largeTabletPosition,
        largeViewportPosition,
    ]);
    // Memoize the SpotlightBanner component to prevent re-renders when favorites change
    const MemoizedSpotlightBanner = useMemo(() => {
        if (!spotlightBanner)
            return null;
        return _jsx(SpotlightBanner, Object.assign({}, spotlightBanner, { sendSpotlightBannerAnalytics: sendSpotlightBannerAnalytics }));
    }, [spotlightBanner, sendSpotlightBannerAnalytics]);
    const SpotLightBannerWrapper = useCallback(({ index }) => {
        return spotlightBannerPosition === index ? MemoizedSpotlightBanner : null;
    }, [spotlightBannerPosition, MemoizedSpotlightBanner]);
    const { state: { items: favourites }, } = useFavorites();
    // Memoize favorites length to avoid unnecessary re-renders
    const favouritesLength = useMemo(() => favourites.length, [favourites.length]);
    // Track which card should show tooltips - these states are isolated from favorites context
    const [favoritedIndex, setFavoritedIndex] = useState(null);
    const [initialTooltipIndex, setInitialTooltipIndex] = useState(null);
    const [initialTooltipClosed, setInitialTooltipClosed] = useState(false);
    const [favoritedTooltipClosed, setFavoritedTooltipClosed] = useState(false);
    useEffect(() => {
        if (!shouldShowFavorites) {
            setFavoritedIndex(null);
            setInitialTooltipIndex(null);
            setInitialTooltipClosed(false);
            setFavoritedTooltipClosed(false);
        }
    }, [shouldShowFavorites]);
    const handleFavoriteToggle = useCallback((index) => {
        // if it is favorite page then start the spinner
        if (isFromFavorites) {
            localStorage.setItem('isFavoriteToggle', 'true');
        }
        // Always close initial tooltip on any favorite toggle
        setInitialTooltipIndex(null);
        setInitialTooltipClosed(true);
        if (favourites.length === 0) {
            setFavoritedIndex(index);
        }
        else {
            setFavoritedIndex(null);
            setFavoritedTooltipClosed(true);
        }
    }, [favourites.length, isFromFavorites]);
    const handleInitialTooltipClose = useCallback(() => {
        setInitialTooltipIndex(null);
        setInitialTooltipClosed(true);
    }, []);
    // Memoize tooltip helpers to prevent recreation on every render
    const tooltipHelpers = useMemo(() => ({
        shouldShowInitialTooltip: (index) => {
            if (favouritesLength !== 0 || initialTooltipClosed)
                return false;
            if (initialTooltipIndex === null) {
                if (isLargeTablet) {
                    if ((index === 1 && productsList.length >= 2) || (index === 0 && productsList.length === 1)) {
                        setInitialTooltipIndex(index);
                    }
                }
                else if (index === 0) {
                    setInitialTooltipIndex(index);
                }
            }
            return initialTooltipIndex === index;
        },
        shouldShowFavoritedTooltip: (index) => {
            return favoritedIndex === index && favouritesLength === 1 && !favoritedTooltipClosed;
        },
    }), [
        favouritesLength,
        initialTooltipClosed,
        initialTooltipIndex,
        isLargeTablet,
        productsList.length,
        favoritedIndex,
        favoritedTooltipClosed,
    ]);
    return (_jsxs(Grid2, { className: clsx(classes.productGrid, className !== null && className !== void 0 ? className : ''), container: true, spacing: 3, children: [productsList === null || productsList === void 0 ? void 0 : productsList.map((product, index) => {
                // Always show tooltip on first card on mobile
                // On isLargeTablet, show on second card only if products >= 2
                tooltipHelpers.shouldShowInitialTooltip(index);
                tooltipHelpers.shouldShowFavoritedTooltip(index);
                const handleProductFavoriteToggle = () => handleFavoriteToggle(index);
                return (_jsxs(React.Fragment, { children: [!!spotlightBanner && _jsx(SpotLightBannerWrapper, { index: index }), _jsx(Grid2, { ref: index === 0 ? productCardRef : null, size: {
                                xs: convertBooleanToGridColumn(extraSmall),
                                sm: convertBooleanToGridColumn(small),
                                md: convertBooleanToGridColumn(medium),
                                lg: convertBooleanToGridColumn(large),
                            }, className: clsx(cardDisplayType === 'DELIVERY_PASS' ? classes.deliveryPassGridItem : classes.gridItem, isProductCardRedesignEnabled && classes.gridItemRedesign), children: _jsx(ProductCard, { product: product, highlightTextMatch: highlightTextMatch, imageCarousel: true, cardDisplayType: cardDisplayType, hideBorder: noCardBorder, addToBasket: addToBasket, index: index, setHeight: handleBadgeHeight, isFromSearchCard: isFromSearchCard, onProductClick: onProductClick, pdpRecommendationAnalytics: pdpRecommendationAnalytics, isFromFavorites: isFromFavorites, isEnableTrustpilotReviews: isEnableTrustpilotReviews, nameTag: nameTag, isFromProductCarousel: isFromProductCarousel, showFavoriteSelect: shouldShowFavorites, showInitialTooltip: tooltipHelpers.shouldShowInitialTooltip(index), showFavoritedTooltip: tooltipHelpers.shouldShowFavoritedTooltip(index), onFavoriteToggle: handleProductFavoriteToggle, onInitialTooltipClose: handleInitialTooltipClose, sendFavouritingProductAnalytics: sendFavouritingProductAnalytics, imageShape: imageShape }) })] }, `${index}${product.name}`));
            }), !!spotlightBanner && (productsList === null || productsList === void 0 ? void 0 : productsList.length) && (productsList === null || productsList === void 0 ? void 0 : productsList.length) === totalResultsCount ? (_jsx(SpotLightBannerWrapper, { index: (productsList === null || productsList === void 0 ? void 0 : productsList.length) || -1 })) : (''), shouldShowSkeletons && (_jsx(SkeletonProductCard, { count: Math.min(pendingProductCount !== null && pendingProductCount !== void 0 ? pendingProductCount : 12, 12), breakpoints: {
                    extraSmall,
                    small,
                    medium,
                    large,
                }, spotlightBanner: spotlightBanner, cardHeight: productCardHeight, isNewDesignLayout: true }))] }));
};
export default ProductGrid;
